import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function Auth() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "login" ? "login" : "register";
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState(params.get("ref") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = mode === "login"
      ? await login(email, password)
      : await register(name, email, password, referralCode || undefined);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success(mode === "login" ? "Welcome back." : "Welcome. Let's begin.");
    navigate("/app");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left column: form */}
      <div className="flex flex-col justify-between p-8 md:p-16">
        <Link to="/" className="flex items-center" data-testid="auth-logo">
          <Logo size={90} />
        </Link>
        <div className="max-w-md w-full mx-auto md:mx-0">
          <div className="label-eyebrow mb-6">{mode === "login" ? "Return" : "Begin"}</div>
          <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] mb-10">
            {mode === "login" ? (
              <>Welcome <em className="italic">back</em>.</>
            ) : (
              <>Come <em className="italic text-[hsl(var(--primary))]">home</em>.</>
            )}
          </h1>
          <form onSubmit={submit} className="space-y-6" data-testid="auth-form">
            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/app";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="btn-google-signin"
              className="w-full border border-[hsl(var(--foreground))] py-3 hover:bg-[hsl(var(--foreground))] hover:text-white transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <span className="font-serif text-lg">G</span> Continue with Google
            </button>
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center"><span className="bg-[hsl(var(--background))] px-4 label-eyebrow">or with email</span></div>
            </div>
            {mode === "register" && (
              <div>
                <label className="label-eyebrow block mb-2">Name</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="auth-name"
                  className="w-full bg-transparent border-0 border-b border-border py-3 no-shadow-focus text-lg"
                  placeholder="What shall we call you?"
                />
              </div>
            )}
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email"
                className="w-full bg-transparent border-0 border-b border-border py-3 no-shadow-focus text-lg"
                placeholder="you@yourself.com"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password"
                className="w-full bg-transparent border-0 border-b border-border py-3 no-shadow-focus text-lg"
                placeholder="At least six characters"
              />
            </div>
            {mode === "register" && (
              <div>
                <label className="label-eyebrow block mb-2">Referral code (optional)</label>
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  data-testid="auth-ref"
                  className="w-full bg-transparent border-0 border-b border-border py-3 no-shadow-focus text-lg tracking-widest uppercase"
                  placeholder="From a friend"
                />
                <div className="text-xs text-stone-500 mt-1">Your friend earns a free month when you subscribe.</div>
              </div>
            )}
            {error && <div className="text-sm text-[hsl(var(--destructive))]" data-testid="auth-error">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit"
              className="w-full bg-[hsl(var(--primary))] text-white py-4 hover:bg-[hsl(var(--foreground))] transition-colors disabled:opacity-60"
            >
              {loading ? "…" : mode === "login" ? "Sign in" : "Begin the practice"}
            </button>
          </form>
          <div className="mt-10 text-sm text-stone-500">
            {mode === "login" ? (
              <>New here? <button data-testid="auth-switch-register" onClick={() => setMode("register")} className="underline hover:text-[hsl(var(--primary))]">Begin instead</button></>
            ) : (
              <>Already have an account? <button data-testid="auth-switch-login" onClick={() => setMode("login")} className="underline hover:text-[hsl(var(--primary))]">Sign in</button></>
            )}
          </div>
        </div>
        <div />
      </div>
      {/* Right column: image */}
      <div className="hidden md:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1579017308347-e53e0d2fc5e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHx3cml0aW5nJTIwaW4lMjBtZWRpdGF0aW9uJTIwam91cm5hbCUyMHdhcm18ZW58MHx8fHwxNzg1OTU2NDIxfDA&ixlib=rb-4.1.0&q=85"
          alt="Journaling in warm light"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-8 left-8 right-8 bg-[hsl(var(--background))]/95 border border-border p-6 max-w-sm">
          <div className="label-eyebrow mb-2">Month one · Day one</div>
          <p className="font-serif italic text-lg leading-snug">
            "Why did you choose this journey? Not what your family thinks — what YOU want."
          </p>
        </div>
      </div>
    </div>
  );
}
